import React, { useState, useEffect, useMemo, useCallback } from 'react'
import { Alert, Box, Button, Card, Dialog, DialogActions, DialogContent, InputLabel, MenuItem, Select, Snackbar, Typography } from '@mui/material'
import { useImportTourMutation, useImportTranslationsMutation, useToursQuery } from '@typings/graphql'
import { TourCard } from '@shared/components/ui/TourCard'
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline'
import UploadFileIcon from '@mui/icons-material/UploadFile'
import TranslationUploadIcon from '@mui/icons-material/Translate'
import RecordVoiceOverIcon from '@mui/icons-material/RecordVoiceOver'
import AudioFileIcon from '@mui/icons-material/AudioFile'
import { useNavigate } from 'react-router'
import { useMutation } from '@apollo/client'
import FileDragDrop from '@features/cms/components/FileDragDrop'
import { useTranslation } from 'react-i18next'
import { LoadingButton } from '@mui/lab'
import languages from '@assets/json/languages.json'
import { IMPORT_VOICEOVERS, ImportVoiceoversData, ImportVoiceoversVars } from '@features/voiceover/graphql'

const TourList: React.FC = () => {
  const navigate = useNavigate()

  const { t } = useTranslation()
  const { data, refetch } = useToursQuery()
  const tours = data?.tours

  const [importTour, { loading }] = useImportTourMutation()
  const [importTranslations, { loading: loadingTranslations }] = useImportTranslationsMutation()
  const [importVoiceovers, { loading: loadingVoiceovers }] = useMutation<ImportVoiceoversData, ImportVoiceoversVars>(IMPORT_VOICEOVERS)
  const [tourItemOpenState, setTourItemOpenState] = useState<Record<string, boolean>>({})
  const [showImportDialog, setShowImportDialog] = useState<'tour' | 'translations' | 'voiceovers' | false>(false)
  const [importFile, setImportFile] = useState<File | null>(null)
  const [importLanguage, setImportLanguage] = useState<string>('en')
  const [importError, setImportError] = useState<string | null>(null)
  const [importSuccess, setImportSuccess] = useState<string | null>(null)

  useEffect(() => {
    refetch()
  }, [])

  useEffect(() => {
    tours?.forEach((tour) => { setTourItemOpenState({ ...tourItemOpenState, [tour.id]: false }) })
  }, [tours])

  const performTourImport = useCallback(async () => {
    if (!importFile) return
    setImportError(null)

    try {
      const { data: result } = await importTour({
        variables: { file: importFile },
        fetchPolicy: 'no-cache'
      })

      const title = result?.importTour?.title
      setShowImportDialog(false)
      setImportFile(null)
      setImportSuccess(title ? `${t('tour.importTourSuccess')}: ${title}` : t('tour.importTourSuccess'))
    } catch (e: any) {
      // Keep the dialog open and surface the reason inline.
      setImportError(e?.message ?? String(e))
    }
  }, [importFile])

  const performTranslationsImport = useCallback(async () => {
    if (!importFile) return

    const { data: importResult } = await importTranslations({
      variables: {
        language: importLanguage,
        file: importFile
      },
      fetchPolicy: 'no-cache'
    })

    setShowImportDialog(false)

    alert(`Translations imported.

    Added: ${importResult?.importTranslations?.added}
    Updated: ${importResult?.importTranslations?.updated}
    Unchanged: ${importResult?.importTranslations?.unchanged}`)
  }, [importFile, importLanguage])

  const performVoiceoverImport = useCallback(async () => {
    if (!importFile) return
    setImportError(null)

    try {
      const { data: result } = await importVoiceovers({
        variables: { file: importFile },
        fetchPolicy: 'no-cache'
      })

      const r = result?.importVoiceovers
      setShowImportDialog(false)

      const lines = [
        `Voiceovers importiert für: ${r?.tourTitle || r?.tourId}`,
        '',
        `Eingepflegt: ${r?.matched} / ${r?.total}`
      ]
      if (r?.unmatched?.length) {
        lines.push('', `Nicht zugeordnet (${r.unmatched.length}):`, r.unmatched.join('\n'))
      }
      if (r?.skipped?.length) {
        lines.push('', `Übersprungen – mehrteilig (${r.skipped.length}):`, r.skipped.join('\n'))
      }
      alert(lines.join('\n'))
    } catch (e: any) {
      // Keep the dialog open and show the reason inline (e.g. ZIP not named after a tour id).
      setImportError(e?.message ?? String(e))
    }
  }, [importFile])

  const tourCards = useMemo(() => (tours || []).map((tour) => (
    <TourCard key={tour.id}
      tour={{
        keyvisual: tour.keyvisual?.url,
        title: tour.title,
        subtitle: tour.subtitle,
        rating: tour.rating
      }}
      onTourSelect={() => navigate(`/tour/${tour.id}`)}
    />
  )), [tours])

  const createTourCard = useMemo(() => (
    <Card
      key='create-tour'
      sx={{
        position: 'relative',
        height: '430px',
        aspectRatio: '2/3',
        cursor: 'pointer',
        borderRadius: '15px',
        display: 'flex',
        flexDirection: 'column',
        gap: '10px',
        alignItems: 'center',
        justifyContent: 'center'
      }}
      onClick={() => navigate('/tour/create')}
    >
      <AddCircleOutlineIcon />
      <Typography>{t('edit.tour.create')}</Typography>
    </Card>
  ), [])

  const importTourCard = useMemo(() => (
    <Card
      key='import-tour'
      sx={{
        position: 'relative',
        height: '430px',
        aspectRatio: '2/3',
        cursor: 'pointer',
        borderRadius: '15px',
        display: 'flex',
        flexDirection: 'column',
        gap: '10px',
        alignItems: 'center',
        justifyContent: 'center'
      }}
      onClick={() => setShowImportDialog('tour')}
    >
      <UploadFileIcon />
      <Typography>{t('tour.importTour')}</Typography>
    </Card>
  ), [])

  const importTranslationsCard = useMemo(() => (
    <Card
      key='import-translations'
      sx={{
        position: 'relative',
        height: '430px',
        aspectRatio: '2/3',
        cursor: 'pointer',
        borderRadius: '15px',
        display: 'flex',
        flexDirection: 'column',
        gap: '10px',
        alignItems: 'center',
        justifyContent: 'center'
      }}
      onClick={() => setShowImportDialog('translations')}
    >
      <TranslationUploadIcon />
      <Typography>{t('edit.tour.importTranslations')}</Typography>
      <Typography sx={{
        opacity: 0.5,
        fontSize: '0.8rem'
      }}
      >(*.json)</Typography>
    </Card>
  ), [])

  const voiceoverCard = useMemo(() => (
    <Card
      key='voiceover'
      sx={{
        position: 'relative',
        height: '430px',
        aspectRatio: '2/3',
        cursor: 'pointer',
        borderRadius: '15px',
        display: 'flex',
        flexDirection: 'column',
        gap: '10px',
        alignItems: 'center',
        justifyContent: 'center'
      }}
      onClick={() => navigate('/voiceover')}
    >
      <RecordVoiceOverIcon />
      <Typography>{t('tour.voiceover')}</Typography>
    </Card>
  ), [])

  const voiceoverUploadCard = useMemo(() => (
    <Card
      key='voiceover-upload'
      sx={{
        position: 'relative',
        height: '430px',
        aspectRatio: '2/3',
        cursor: 'pointer',
        borderRadius: '15px',
        display: 'flex',
        flexDirection: 'column',
        gap: '10px',
        alignItems: 'center',
        justifyContent: 'center'
      }}
      onClick={() => setShowImportDialog('voiceovers')}
    >
      <AudioFileIcon />
      <Typography>{t('tour.voiceoverUpload')}</Typography>
      <Typography sx={{ opacity: 0.5, fontSize: '0.8rem' }}>(*.zip)</Typography>
    </Card>
  ), [])

  return (
    <Box
      height="100%"
      padding={6}
      display="flex"
      flexWrap="wrap"
      gap="20px"
      sx={{ overflowY: 'auto' }}
    >
      {tourCards.concat(createTourCard, importTourCard, importTranslationsCard, voiceoverCard, voiceoverUploadCard)}

      <Dialog open={!!showImportDialog} PaperProps={{ sx: { maxWidth: '800px', width: '60vw' } }}>
        <DialogContent sx={{ width: '100%' }}>
          <Box>
            <Typography variant="h5">
              {showImportDialog === 'tour'
                ? t('tour.importTour')
                : showImportDialog === 'voiceovers'
                  ? t('tour.voiceoverUpload')
                  : t('edit.tour.importTranslations')}
            </Typography>
            {showImportDialog === 'voiceovers'
              ? (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  ZIP mit den Aufnahmen ablegen. Der Ordner/das ZIP muss nach der Tour-ID benannt sein;
                  die Dateien werden anhand ihres Namens automatisch der Tour zugeordnet.
                </Typography>
                )
              : null}
            {showImportDialog === 'translations'
              ? (
                <Box mt={2}>
                  <InputLabel id="import-language">{t('common.language')}</InputLabel>
                  <Select
                    autoWidth
                    labelId="import-language"
                    defaultValue='en'
                    maxRows={10}
                    value={importLanguage}
                    onChange={(e) => setImportLanguage(e.target.value as string)}
                  >
                    {languages.map((language) => (
                      <MenuItem key={language.code} value={language.code}>{language.name} ({language.code})</MenuItem>
                    ))}
                  </Select>
                </Box>
                )
              : null}
            <Box width="100%" mt={2}>
              <FileDragDrop
                accept={showImportDialog === 'translations' ? { 'application/json': ['.json'] } : { 'application/zip': ['.zip'] }}
                onFileChanged={(f) => { setImportFile(f); setImportError(null) }}
                maxFileSize={2000000000}
                height={100}
              >
                <Typography color={importFile ? 'text.primary' : 'text.secondary'}>
                  {importFile
                    ? `Ausgewählt: ${importFile.name}`
                    : showImportDialog === 'translations'
                      ? 'JSON-Datei hierher ziehen oder klicken'
                      : 'ZIP-Datei hierher ziehen oder klicken'}
                </Typography>
              </FileDragDrop>
              {importError
                ? (
                  <Typography color="error" variant="body2" sx={{ mt: 1, whiteSpace: 'pre-wrap' }}>
                    {importError}
                  </Typography>
                  )
                : null}
            </Box>
            {importFile && (
              <Typography variant="body2" sx={{ mt: 1 }}>
                {t('tour.selectedFile', { name: importFile.name })}
              </Typography>
            )}
            {importError && (
              <Alert severity="error" sx={{ mt: 1, whiteSpace: 'pre-wrap' }}>{importError}</Alert>
            )}
          </Box>
        </DialogContent>

        <DialogActions sx={{ p: 4, pt: 0 }}>
          <Button
            variant="outlined"
            type="button"
            disabled={loading}
            onClick={() => { setShowImportDialog(false); setImportError(null) }}
          >
            {t('common.cancel')}
          </Button>

          <LoadingButton
            variant="contained"
            type="button"
            disabled={!importFile || loading}
            loading={loading || loadingTranslations || loadingVoiceovers}
            onClick={
              showImportDialog === 'tour'
                ? performTourImport
                : showImportDialog === 'voiceovers'
                  ? performVoiceoverImport
                  : performTranslationsImport
            }
          >
            {t('common.save')}
          </LoadingButton>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={!!importSuccess}
        autoHideDuration={6000}
        onClose={() => setImportSuccess(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity="success" onClose={() => setImportSuccess(null)} sx={{ whiteSpace: 'pre-wrap' }}>
          {importSuccess}
        </Alert>
      </Snackbar>
    </Box>
  )
}

export default TourList
